import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, BadgeCheck, Power } from "lucide-react";
import { fetchAdminLawyers, verifyLawyer, updateLawyer } from "@admin/services/adminApi";
import { PageHeader, DataTable, Pagination, FilterBar, Pill } from "@admin/components/Primitives";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LIMIT = 20;

export default function AdminLawyers() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchAdminLawyers({ q: q || undefined, verified, skip, limit: LIMIT });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load lawyers"); }
    setLoading(false);
  }, [q, verified, skip]);

  useEffect(() => { load(); }, [load]);

  const toggleVerified = async (l) => {
    try {
      await verifyLawyer(l.id, !l.verified);
      toast.success(`${l.name} ${!l.verified ? "verified" : "un-verified"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleAvailable = async (l) => {
    try {
      await updateLawyer(l.id, { available: !l.available });
      toast.success(`${l.name} marked ${!l.available ? "online" : "offline"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateFee = async (l, fee) => {
    if (!fee || isNaN(Number(fee))) return;
    try {
      await updateLawyer(l.id, { fee: Number(fee) });
      toast.success(`Updated base fee for ${l.name}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    {
      key: "name",
      label: "Lawyer",
      render: (r) => (
        <div className="min-w-[200px]">
          <p className="font-bold text-[#0A2540]">{r.name}</p>
          <p className="text-xs text-slate-500">{r.specialization} · {r.city}</p>
        </div>
      ),
    },
    { key: "email", label: "Email", render: (r) => <span className="text-xs text-slate-500">{r.email}</span> },
    {
      key: "fee",
      label: "Base fee",
      render: (r) => (
        <input
          type="number"
          defaultValue={r.fee}
          onBlur={(e) => Number(e.target.value) !== r.fee && updateFee(r, e.target.value)}
          className="w-20 px-2 py-1 border border-slate-200 rounded-md text-sm"
          data-testid={`lawyer-fee-${r.id}`}
        />
      ),
    },
    { key: "rating", label: "Rating", render: (r) => <span className="font-bold">★ {Number(r.rating || 0).toFixed(1)}</span> },
    {
      key: "available",
      label: "Status",
      render: (r) => (
        <div className="flex flex-col gap-1">
          {r.verified ? <Pill color="emerald"><BadgeCheck size={10} /> Verified</Pill> : <Pill color="amber">Pending</Pill>}
          {r.available ? <Pill color="emerald">Online</Pill> : <Pill color="slate">Offline</Pill>}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="flex gap-1">
          <button onClick={() => toggleVerified(r)} className="px-2 py-1 text-xs font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`verify-toggle-${r.id}`}>
            {r.verified ? "Unverify" : "Verify"}
          </button>
          <button onClick={() => toggleAvailable(r)} className={`px-2 py-1 text-xs font-bold rounded-md border ${r.available ? "border-emerald-300 text-emerald-700" : "border-slate-200"}`} data-testid={`avail-toggle-${r.id}`}>
            <Power size={10} className="inline" /> {r.available ? "Online" : "Offline"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader testid="admin-lawyers-header" title="Lawyers" subtitle="Verify, edit fees, and toggle availability." />
      <FilterBar testid="lawyers-filterbar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setSkip(0); setQ(e.target.value); }} placeholder="Search by name, city, specialization…" className="pl-8" data-testid="lawyers-search" />
        </div>
        <Select value={verified} onValueChange={(v) => { setSkip(0); setVerified(v); }}>
          <SelectTrigger className="w-[160px]" data-testid="lawyers-filter-verified"><SelectValue placeholder="Verified" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Verified</SelectItem>
            <SelectItem value="false">Pending</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <DataTable testid="lawyers-table" columns={columns} rows={data.items} empty={loading ? "Loading…" : "No lawyers match"} />
      <Pagination testid="lawyers-pagination" total={data.total} skip={skip} limit={LIMIT} onChange={setSkip} />
    </>
  );
}
